// src/Menu.js
import React, { useState, useEffect, useMemo } from "react";
import "./Menu.css";
import Modal from "./Modal";

import { db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";

const HomeOld = () => {
  const [menuData, setMenuData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [buttonDetail, setButtonDetail] = useState(null);

  // NEW: separate global search and topic-level search
  const [globalSearchText, setGlobalSearchText] = useState("");
  const [topicSearchText, setTopicSearchText] = useState("");

  const [hideButtons, setHideButtons] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // helper: prettify title if missing
  const prettyTitle = (slug, titleField) => {
    if (titleField) return titleField;
    return String(slug || "")
      .replace(/-/g, " ")
      .toUpperCase();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const colRef = collection(db, "interview");
        const snap = await getDocs(colRef);

        const items = snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const questions = Array.isArray(data.questions)
            ? data.questions
            : [];

          // 1a) De-dupe by stable `id` if present
          const seenIds = new Set();
          const uniqueById = [];
          for (const q of questions) {
            const idKey = q.id ?? `${(q.topic || '').trim().toLowerCase()}::${(q.question || '').trim().toLowerCase()}`;
            if (!seenIds.has(idKey)) {
              seenIds.add(idKey);
              uniqueById.push(q);
            }
          }

          // 1b) Also guard against duplicate question texts (optional)
          const seenTexts = new Set();
          const uniqueQuestions = [];
          for (const q of uniqueById) {
            const textKey = (q.question || '').trim().toLowerCase();
            if (!seenTexts.has(textKey)) {
              seenTexts.add(textKey);
              uniqueQuestions.push(q);
            }
          }


          return {
            id: docSnap.id,
            title: prettyTitle(docSnap.id, data.title),
            description: data.description || "",
            questions: uniqueQuestions, // array of { id, topic, question, answer, order, usecase, scenario }
          };
        });

        // sort topics by title
        items.sort((a, b) =>
          a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        );

        setMenuData(items);
      } catch (err) {
        console.error("Error fetching interview data from Firestore:", err);
      }
    };

    fetchData();
  }, []);

  const handleMenuClick = (id) => {
    const selected = menuData.find((item) => item.id === id) || null;
    setSelectedItem(selected);
    setButtonDetail(null);
    setTopicSearchText(""); // reset topic search
    setHideButtons(false);

    // optional: clear global search when user clicks a topic
    // setGlobalSearchText("");
  };

  const handleClearSearch = () => {
    setGlobalSearchText("");
    setTopicSearchText("");
    setHideButtons(false);
    setButtonDetail(null); // optional: close last selected question
  };


  const buildDetailText = (q) => {
    const parts = [];

    if (q.answer) {
      parts.push(`${q.answer}`);
    }

    if (q.usecase) {
      parts.push(`Use case:\n${q.usecase}`);
    }

    if (q.scenario || q.scanario) {
      const s = q.scenario || q.scanario;
      parts.push(`Scenario:\n${s}`);
    }

    return parts.join("\n\n");
  };

  const handleButtonClick = (questionObj) => {
    const detailText = buildDetailText(questionObj);
    setButtonDetail(detailText);
    setHideButtons(true);
    setIsModalOpen(true);
  };

  // GLOBAL SEARCH: handle typing in left search box
  const handleGlobalSearchChange = (e) => {
    const value = e.target.value;
    setGlobalSearchText(value);
    setButtonDetail(null);
    setHideButtons(false);
  };

  // TOPIC SEARCH: right side search inside selected topic
  const handleTopicSearchChange = (e) => {
    const value = e.target.value;
    setTopicSearchText(value);
    if (value === "") {
      setHideButtons(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // 1) GLOBAL SEARCH RESULTS (across ALL topics & questions)
  // const globalResults = useMemo(() => {
  //   const search = globalSearchText.trim().toLowerCase();
  //   if (!search) return [];

  //   const results = [];

  //   menuData.forEach((topic) => {
  //     (topic.questions || []).forEach((q) => {
  //       const qText = (q.question || "").toLowerCase();
  //       const tText = (q.topic || "").toLowerCase();

  //       if (qText.includes(search) || tText.includes(search)) {
  //         results.push({
  //           topicId: topic.id,
  //           topicTitle: topic.title,
  //           question: q,
  //         });
  //       }
  //     });
  //   });

  //   return results;
  // }, [globalSearchText, menuData]);


  const globalResults = useMemo(() => {
    const search = globalSearchText.trim().toLowerCase();
    if (!search) return [];

    const seen = new Set();
    const results = [];

    menuData.forEach((topic) => {
      (topic.questions || []).forEach((q) => {
        const qText = (q.question || '').toLowerCase();
        const tText = (q.topic || '').toLowerCase();

        if (qText.includes(search) || tText.includes(search)) {
          // Use a stable key: prefer q.id; fallback to normalized text
          const key = q.id ?? `${qText}::${tText}`;
          if (!seen.has(key)) {
            seen.add(key);
            results.push({
              topicId: topic.id,
              topicTitle: topic.title,
              question: q,
            });
          }
        }
      });
    });

    return results;
  }, [globalSearchText, menuData]);


  // 2) TOPIC-LEVEL FILTERED CONTENT (used when NO global search)
  // const filteredContent =
  //   selectedItem && !globalSearchText.trim()
  //     ? {
  //       title: selectedItem.title,
  //       description:
  //         selectedItem.description &&
  //           selectedItem.description
  //             .toLowerCase()
  //             .includes(topicSearchText.toLowerCase())
  //           ? selectedItem.description
  //           : "",
  //       questions: (selectedItem.questions || []).filter((q) => {
  //         const qText = (q.question || "").toLowerCase();
  //         const tText = (q.topic || "").toLowerCase();
  //         const search = topicSearchText.toLowerCase();
  //         return qText.includes(search) || tText.includes(search);
  //       }),
  //     }
  //     : null;

  const filteredContent =
    selectedItem && !globalSearchText.trim()
      ? (() => {
        const search = topicSearchText.toLowerCase();
        const seen = new Set();

        const qs = (selectedItem.questions || [])
          .filter((q) => {
            const qText = (q.question || '').toLowerCase();
            const tText = (q.topic || '').toLowerCase();
            return qText.includes(search) || tText.includes(search);
          })
          .filter((q) => {
            const key = q.id ?? (q.question || '').trim().toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        const desc =
          selectedItem.description &&
            selectedItem.description.toLowerCase().includes(search)
            ? selectedItem.description
            : '';

        return {
          title: selectedItem.title,
          description: desc,
          questions: qs,
        };
      })()
      : null;

      
const formatText = (text) => {
  if (!text) return "";
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};




  return (
    <div>
      {/* Left Menu */}
      <nav className="menu">
        <div className="top-search-section mt-2">
          {/* GLOBAL SEARCH INPUT */}
          <input
            type="text"
            value={globalSearchText}
            onChange={handleGlobalSearchChange}
            placeholder="Global search for all topics..."
            className="search-input"
          />
          <button
            type="button"
            className="clear-btn btn btn-sm btn-secondary ms-2"
            onClick={handleClearSearch}
          >
            Clear
          </button>


            {globalResults.length > 0 ? (
              <div className="button-section">
                {globalResults.map((res, idx) => (
                  <button
                    // key={res.question.id || idx}
                    key={res.question.id ?? `${res.topicId}::${(res.question.question || '').trim().toLowerCase()}::${idx}`}
                    onClick={() => handleButtonClick(res.question)}
                    className="button-detail btn btn-primary btn-sm"
                  >
                    {/* Show topic name + question text */}
                    [{res.topicTitle}] &nbsp;
                    <strong>{res.question.question || res.question.topic}</strong>
                  </button>

                ))}
              </div>

            ) : (
              <p>No results found across all topics.</p>
            )}


        </div>




        <ul className="menu-list mt-2">
          {menuData.length > 0 ? (
            menuData.map((item) => (
              <li className="menu-item " key={item.id}>
                <button onClick={() => handleMenuClick(item.id)}>
                  {formatText(item.title)}
                </button>
              </li>
            ))
          ) : (
            <p>Loading topics from Firebase...</p>
          )}
        </ul>
      </nav>

      {/* Right Content */}
      <div className="content">
        {/* PRIORITY 1: SHOW GLOBAL SEARCH RESULTS IF USER TYPED SOMETHING */}
        {globalSearchText.trim() ? (
          <div>
            <h2>
              Search results for: "<span>{formatText(globalSearchText)}</span>"
            </h2>

            {globalResults.length > 0 ? (
              <div className="button-section">
                {globalResults.map((res, idx) => (
                  <button
                    // key={res.question.id || idx}
                    // key={res.question.id ?? `${res.topicId}::${(res.question.question || '').trim().toLowerCase()}::${idx}`}
                    // onClick={() => handleButtonClick(res.question)}
                    // className="button-detail btn btn-primary btn-sm"
                  >
                    {/* Show topic name + question text */}
                    {/* [{res.topicTitle}] &nbsp; */}
                    {/* <strong>{res.question.question || res.question.topic}</strong> */}
                  </button>

                ))}
              </div>

            ) : (
              <p>No results found across all topics.</p>
            )}
          </div>
        ) : (
          // PRIORITY 2: NORMAL TOPIC VIEW WHEN THERE IS NO GLOBAL SEARCH
          <>
            {selectedItem ? (
              <div>
                {/* <h2>{filteredContent.title}</h2>

                {selectedItem.description && (
                  <p className="topic-description">
                    {selectedItem.description}
                  </p>
                )} */}

                {/* Topic-level search inside selected topic */}
                <div className="search-section">
                  <input
                    type="text"
                    value={topicSearchText}
                    onChange={handleTopicSearchChange}
                    placeholder="Search questions in this topic..."
                    className="search-input"
                  />

                  <button
                    type="button"
                    className="clear-btn btn btn-sm btn-secondary ms-2"
                    onClick={handleClearSearch}
                  >
                    Clear
                  </button>
                </div>



                {!hideButtons && filteredContent.questions.length > 0 ? (
                  <div className="button-section">
                    {filteredContent.questions
                      .slice() // shallow copy before sort
                      .sort((a, b) => {
                        // sort by order if present, else by question text
                        if (
                          typeof a.order === "number" &&
                          typeof b.order === "number"
                        ) {
                          return a.order - b.order;
                        }
                        return (a.question || "")
                          .toLowerCase()
                          .localeCompare((b.question || "").toLowerCase());
                      })
                      .map((q, idx) => (
                        <button
                          // key={q.id || idx}
                          key={q.id ?? `${selectedItem.id}::${(q.question || '').trim().toLowerCase()}::${idx}`}
                          onClick={() => handleButtonClick(q)}
                          className="button-detail btn btn-secondary btn-sm"
                        >
                          {/* {q.question || q.topic} */}
                          {formatText(q.question || q.topic)}
                        </button>
                      ))}
                  </div>
                ) : buttonDetail ? null : (
                  <p>No questions available for this topic.</p>
                )}
              </div>
            ) : (
              <p>Please select a topic from the left menu.</p>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && <Modal content={buttonDetail} onClose={closeModal} />}
    </div>
  );
};

export default HomeOld;
